import {
  Tabs,
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  message,
  InputNumber,
  Popconfirm,
  Space,
  Upload
} from "antd";

import { PlusOutlined } from "@ant-design/icons";

import { useEffect, useState } from "react";
import api from "../../services/api";

export default function CatalogMaster() {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeType, setActiveType] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  const [activeTab, setActiveTab] = useState("subcategories");
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    loadCategories();
    loadSubCategories();
    loadBrands();
    loadModels();
  }, []);

  /* ---------------- LOAD DATA ---------------- */

  const loadCategories = async () => {
    const res = await api.get("/categories");
    setCategories(res.data || []);
  };

  const loadSubCategories = async () => {
    const res = await api.get("/catalog/subcategories");
    setSubCategories(res.data || []);
  };

  const loadBrands = async () => {
    const res = await api.get("/catalog/brands");
    setBrands(res.data || []);
  };

  const loadModels = async () => {
    const res = await api.get("/catalog/models");
    setModels(res.data || []);
  };

  /* ---------------- CREATE ---------------- */

  const handleCreate = (type) => {
    setActiveType(type);
    setEditingItem(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = (type, record) => {
    setActiveType(type);
    setEditingItem(record);
    form.setFieldsValue({
      ...record,
      image: record.image
        ? [
            {
              uid: "-1",
              name: "image",
              status: "done",
              url: record.image
            }
          ]
        : []
    });
    setDrawerOpen(true);
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async (type, id) => {

    if (type === "subcategory") {
      try {
        await api.delete(`/catalog/subcategories/${id}`);
        message.success("SubCategory deleted");
        loadSubCategories();
      } catch (err) {
        message.error(
          err.response?.data?.message ||
          "Failed to delete SubCategory"
        );
      }
    }

    if (type === "brand") {
      try {
        await api.delete(`/catalog/brands/${id}`);
        message.success("Brand deleted");
        loadBrands();
      } catch (err) {
        message.error(
          err.response?.data?.message ||
          "Failed to delete Brand"
        );
      }
    }

    if (type === "model") {
      try {
        await api.delete(`/catalog/models/${id}`);
        message.success("Model deleted");
        loadModels();
      } catch (err) {
        message.error(
          err.response?.data?.message ||
          "Failed to delete Model"
        );
      }
    }

  };

  /* ---------------- SAVE ---------------- */

  const handleSubmit = async (values) => {
    try {
      if (activeType === "subcategory") {
          const formData = new FormData();
          formData.append("name", values.name);
          formData.append("category_id", values.category_id);
          formData.append("base_price", values.base_price || 0);
          formData.append("discount_price", values.discount_price || 0);
          formData.append("description", values.description || "");

          // 👇 IMAGE handling
          if (values.image && values.image.length > 0) {
            formData.append("image", values.image[0].originFileObj);
          }

          if (editingItem) {
            await api.put(`/catalog/subcategories/${editingItem.id}`, formData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
            message.success("SubCategory updated");
          } else {
            await api.post("/catalog/subcategories", formData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
            message.success("SubCategory created");
          }

          loadSubCategories();
        }

      if (activeType === "brand") {

        if (editingItem) {
          await api.put(`/catalog/brands/${editingItem.id}`, values);
          message.success("Brand updated");
        } else {
          await api.post("/catalog/brands", values);
          message.success("Brand created");
        }

        loadBrands();
      }

      if (activeType === "model") {

        // 🔴 Duplicate check
        const isDuplicate = models.some(m =>
          m.name.toLowerCase() === values.name.toLowerCase() &&
          m.brand_id === values.brand_id &&
          (!editingItem || m.id !== editingItem.id)
        );

        if (isDuplicate) {
          message.error("Model already exists for this brand");
          return;
        }

        if (editingItem) {
          await api.put(`/catalog/models/${editingItem.id}`, values);
          message.success("Model updated");
        } else {
          await api.post("/catalog/models", values);
          message.success("Model created");
        }

        loadModels();
      }

      setDrawerOpen(false);

    } catch {
      message.error("Operation failed");
    }

  };

  const filteredSubCategories = subCategories.filter(item =>
    item.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredBrands = brands.filter(item =>
    item.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredModels = models.filter(item => {
    const matchName = item.name?.toLowerCase().includes(searchText.toLowerCase());
    const matchBrand = selectedBrand ? item.brand_id === selectedBrand : true;
    return matchName && matchBrand;
  });
  /* ---------------- TABLE COLUMNS ---------------- */

  const subCategoryColumns = [

  { title: "Name", dataIndex: "name" },

  {
    title: "Category",
    render: (_, r) => r.category?.name
  },

  {
    title: "Price",
    dataIndex: "base_price"
  },

  {
    title: "Discount",
    dataIndex: "discount_price"
  },

  {
    title: "Description",
    dataIndex: "description",
    render: (text) => text || "-"
  },
  {
    title: "Image",
    dataIndex: "image",
    render: (img) =>
      img ? (
        <img
          src={img}
          alt="sub"
          style={{ width: 50, height: 50, objectFit: "cover" }}
        />
      ) : "-"
  },
  {
    title: "Action",
    render: (_, record) => (
      <Space>

        <Button
          onClick={() => handleEdit("subcategory", record)}
        >
          Edit
        </Button>

        <Popconfirm
          title="Delete SubCategory?"
          onConfirm={() =>
            handleDelete("subcategory", record.id)
          }
        >
          <Button danger>Delete</Button>
        </Popconfirm>

      </Space>
    )
  }

];

  const brandColumns = [

    { title: "Name", dataIndex: "name" },

    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Delete Brand?"
            onConfirm={() =>
              handleDelete("brand", record.id)
            }
          >
            <Button danger>Delete</Button>
          </Popconfirm>

        </Space>
      )
    }

  ];

  const modelColumns = [

    { title: "Name", dataIndex: "name" },

    {
      title: "Brand",
      render: (_, r) => r.brand?.name
    },

    {
      title: "Action",
      render: (_, record) => (
        <Space>

          <Popconfirm
            title="Delete Model?"
            onConfirm={() =>
              handleDelete("model", record.id)
            }
          >
            <Button danger>Delete</Button>
          </Popconfirm>

        </Space>
      )
    }

  ];

  /* ---------------- UI ---------------- */

  return (
    <>
      <Input.Search
        placeholder={
          activeTab === "subcategories"
            ? "Search SubCategory..."
            : activeTab === "brands"
            ? "Search Brand..."
            : "Search Model..."
        }
        style={{ width: 300, marginBottom: 15, position:"absolute", right:"20px", zIndex:9 }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <Tabs
        defaultActiveKey="subcategories"
        onChange={(key) => {
          setActiveTab(key);
          setSearchText("");
          setSelectedBrand(null); // 👈 add this
        }}
      >

        {/* SUBCATEGORY TAB */}

        <Tabs.TabPane tab="SubCategories" key="subcategories">

          <Button
            type="primary"
            onClick={() => handleCreate("subcategory")}
          >
            Add SubCategory
          </Button>

          <Table
            rowKey="id"
            dataSource={filteredSubCategories}
            columns={subCategoryColumns}
            style={{ marginTop: 20 }}
          />

        </Tabs.TabPane>

        {/* BRAND TAB */}

        <Tabs.TabPane tab="Brands" key="brands">

          <Button
            type="primary"
            onClick={() => handleCreate("brand")}
          >
            Add Brand
          </Button>

          <Table
            rowKey="id"
            dataSource={filteredBrands}
            columns={brandColumns}
            style={{ marginTop: 20 }}
          />

        </Tabs.TabPane>

        {/* MODEL TAB */}

        <Tabs.TabPane tab="Models" key="models">

          <Button
            type="primary"
            onClick={() => handleCreate("model")}
          >
            Add Model
          </Button>

          <Select
            placeholder="Filter by Brand"
            allowClear
            style={{ width: 200, marginBottom: 10 }}
            value={selectedBrand}
            onChange={(value) => setSelectedBrand(value)}
            options={brands.map(b => ({
              value: b.id,
              label: b.name
            }))}
          />

          <Table
            rowKey="id"
            dataSource={filteredModels}
            columns={modelColumns}
            style={{ marginTop: 20 }}
          />

        </Tabs.TabPane>

      </Tabs>

      {/* DRAWER */}

      <Drawer
        title={`${editingItem ? "Edit" : "Add"} ${activeType}`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >

        <Form layout="vertical" form={form} onFinish={handleSubmit}>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          {activeType === "subcategory" && (
            <>

            <Form.Item
            name="category_id"
            label="Category"
            rules={[{ required: true }]}
            >
            <Select
                options={categories.map(c => ({
                value: c.id,
                label: c.name
                }))}
            />
            </Form.Item>

            <Form.Item
            name="base_price"
            label="Price"
            >
            <InputNumber
                style={{ width: "100%" }}
                min={0}
            />
            </Form.Item>

            <Form.Item
            name="discount_price"
            label="Discount Price"
            >
            <InputNumber
                style={{ width: "100%" }}
                min={0}
            />
            </Form.Item>

            <Form.Item
            name="description"
            label="Description"
            >
            <Input.TextArea
                rows={4}
                placeholder="Enter subcategory description"
            />
            </Form.Item>

            <Form.Item
              name="image"
              label="SubCategory Image"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) return e;
                return e?.fileList;
              }}
            >
              <Upload
                listType="picture-card"
                beforeUpload={() => false} // prevent auto upload
                maxCount={1}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>

        </>
        )}

          {activeType === "model" && (
            <Form.Item
              name="brand_id"
              label="Brand"
              rules={[{ required: true }]}
            >
              <Select
                options={brands.map(b => ({
                  value: b.id,
                  label: b.name
                }))}
              />
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit">
            Save
          </Button>

        </Form>

      </Drawer>
    </>
  );
}